/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnInit, inject} from '@angular/core';
import * as _ from 'lodash';

import template from './order-list-page.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {OrderListFiltersComponent} from './order-list-filters.component';
import {
  SignalAdminListColumn,
  SignalAdminResourceListState,
  SignalAdminResourceListStateFactory
} from '../common/controller/signal-admin-resource-list-state';
import {getListChildren} from '../common/controller/resource-list-utils';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {LineItemBulkActionService} from '../line-item/line-item-bulk-action.service';
import {LineItemApiService} from '../line-item/line-item-api.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {configureOrderList} from './order-commons';
import {OrderApiService, OrderModel} from './order-api.service';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, OrderListFiltersComponent],
  selector: 'adn-order-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderListPageComponent implements OnInit {
  private readonly listLifetime = inject(DestroyRef);
  @Input() list: SignalAdminResourceListState<OrderModel>;

  @Input()
  set readOnly(value: boolean) {
    (this.list || this.defaultList).readOnly.set(value);
  }

  readonly itemStateName = 'app.adv.orders.order';
  readonly advertiserItemStateName = 'app.adv.advertisers.advertiser';
  readonly teamItemStateName = 'app.adm.teams.team';
  readonly tierItemStateName = 'app.adm.tiers.tier';
  readonly userItemStateName = 'app.adm.users.user';
  readonly lineItemItemStateName = 'app.adv.line-items.line-item';

  readonly childColumns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: 'userState'},
    {param: 'executionState'},
    {param: '', label: 'tier', sortable: false},
    {param: 'objectives', numeric: true},
    {param: 'startDate'},
    {param: 'endDate'},
    {param: 'updateTime'}
  ];
  private readonly defaultList = inject(SignalAdminResourceListStateFactory).create<OrderModel>(
    inject(OrderApiService),
    {listId: 'fullOrderList', baseTable: 'order'},
    inject(DestroyRef)
  );
  readonly childHost: any = {};
  readonly selector = {outputFields: [] as any[], selFields: [] as string[]};
  readonly orderFields: any[];
  readonly defaultFields: any[];
  readonly perms: any;
  readonly hasObjectSource: boolean;
  private readonly api = inject(OrderApiService);

  constructor(
    @Inject(LocalNetworkProfile) localNetworkProfile: any,
    @Inject(LocalUserPermissions) localUserPermissions: any,
    @Inject(AdnTimeShifterService) adnTimeShifter: any,
    private readonly listBinding: SignalResourceListBindingService,
    @Inject(LineItemApiService) private readonly LineItem: LineItemApiService,
    private readonly lineItemBulkActionService: LineItemBulkActionService
  ) {
    this.list = this.defaultList;
    const config: any = {};
    configureOrderList(config, localNetworkProfile, localUserPermissions);
    this.orderFields = config.orderFields;
    this.defaultFields = config.defaultFields;
    this.perms = config.perms;
    this.hasObjectSource = config.hasObjectSource;
    Object.assign(this.list as any, {
      perms: this.perms,
      limitedFilter: config.limitedFilter,
      adnTimeShifter
    });
    getListChildren(this.childHost, listBinding, OBJECT_TYPE.Order, this.LineItem, this.listLifetime);
  }

  get visibleColumns(): SignalAdminListColumn[] {
    const fields = this.selector.outputFields.length ? this.selector.outputFields : this.defaultFields;
    return this.normalizeColumns(fields);
  }

  isFieldVisible(param: string): boolean {
    const selected = this.selector.selFields;
    const fields = selected.length ? selected : this.defaultFields.map((field) => this.fieldId(field));
    return fields.includes(param);
  }

  filtersResolved(): boolean {
    return !this.list.loading();
  }

  toggleChildren(item: any): void {
    this.childHost.getChildren?.(item);
  }

  childList(itemId: string): any {
    return this.childHost.children?.[itemId];
  }

  businessLinkDisabled(): boolean {
    return this.perms.MANAGE_BUSINESS !== true;
  }

  userLinkDisabled(): boolean {
    return this.perms.MANAGE_SYSTEM !== true
      && this.perms.AX_ADVERTISER_ADMIN !== true
      && this.perms.AX_ADVERTISER_TEAM_ADMIN !== true;
  }

  systemLinkDisabled(): boolean {
    return this.perms.MANAGE_SYSTEM !== true;
  }

  eCpmValue(item: any): any {
    const amount = _.get(item, ['spendDelivery', 'amount']);
    const currency = _.get(item, ['spendDelivery', 'currency']);
    const rendered = item?.renderedDelivery;
    if (!_.isFinite(amount) || !rendered) {
      return undefined;
    }
    return {
      amount: amount / rendered * 1000,
      currency
    };
  }

  cpcValue(item: any): any {
    const amount = _.get(item, ['spendDelivery', 'amount']);
    const currency = _.get(item, ['spendDelivery', 'currency']);
    const clicks = item?.clickDelivery;
    if (!_.isFinite(amount) || !clicks) {
      return undefined;
    }
    return {
      amount: amount / clicks,
      currency
    };
  }

  ngOnInit(): void {
    Object.assign(this.list as any, {perms: this.perms});
    this.list.load();
  }

  openBulkActions(): void {
    this.lineItemBulkActionService.createBulkAction({
      bulkSelection: this.list.selected(),
      miscBulkAction: (modal: any, controller: any, items: Partial<OrderModel>[]) => {
        this.api.bulkSave(items).subscribe({
          next: () => {
            modal.close();
            this.list.deselectAll();
            this.list.load();
          },
          error: () => {
            controller.form = controller.form || {};
            controller.form.bulkError = true;
          }
        });
      }
    })();
  }

  private normalizeColumns(fields: any[]): SignalAdminListColumn[] {
    return fields.map((field) => typeof field === 'string' ? {param: field} : {
      param: field.param ?? field.label ?? '',
      label: field.label,
      labelInstant: field.labelInstant,
      numeric: field.numeric,
      sortable: field.sortable
    });
  }

  private fieldId(field: any): string {
    return typeof field === 'string' ? field : field.param || field.label || field;
  }
}
